import type { OnChanges, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { CollectionStateStatuses } from '@common/adapters/collections';
import type { PaymentElement } from '@integrations/payment-methods';
import type { EduCoursePaymentEntity } from '@common/types/payment-form.type';
import type { PaymentElementLegacy } from '@sites/entity-core';
import type { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { TranslateService, TranslateModule } from '@ngx-translate/core';

import { paymentsList, paymentTypeNames } from '@common/constants/payment-form.const';

import { LetDirective } from '@ngrx/component';
import { NgIf, NgFor } from '@angular/common';
import { SpControlHandleComponent } from '@libs/ui/sp-control-handle/src/sp-control-handle.component';

/**
 * Signal-form rewrite of the edu-course payment list. The widget's
 * `paymentElements` now live in the payment-form signal form, so this
 * component is purely value-in / event-out: it reads a plain
 * `PaymentElementLegacy[]` and emits the whole next array
 * (`setPaymentElementsEvent`) — the parent control writes it back through the
 * signal form (`form.setPaymentElements`). Loading the course payments and
 * toggling a method's `isActive` both produce a fresh array.
 */
@Component({
    selector: 'web-edu-course-payments-collection',
    templateUrl: './web-edu-course-payments-collection.component.html',
    styleUrls: ['./web-edu-course-payments-collection.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LetDirective, NgIf, NgFor, SpControlHandleComponent, TranslateModule],
})
export class WebEduCoursePaymentsCollectionComponent implements OnChanges {
    private readonly translateService = inject(TranslateService);
    private readonly destroyRef = inject(DestroyRef);

    @Input() public courseId!: number;
    @Input() public courseTariffId!: number;
    @Input() public paymentElements: PaymentElementLegacy[] = [];
    @Input() public coursePayments$: Observable<EduCoursePaymentEntity[]>;
    @Input() public coursePaymentsStatuses$!: Observable<CollectionStateStatuses>;

    @Output() public setPaymentElementsEvent: EventEmitter<PaymentElementLegacy[]> = new EventEmitter();
    @Output() public setSkipHistoryEvent: EventEmitter<void> = new EventEmitter();

    public ngOnChanges(changes: SimpleChanges): void {
        const { courseId, courseTariffId } = changes;

        const courseChanged = courseId?.currentValue && courseId?.currentValue !== courseId?.previousValue;
        const courseTariffIdChanged =
            courseTariffId?.currentValue && courseTariffId?.currentValue !== courseTariffId?.previousValue;

        if (courseChanged || courseTariffIdChanged) {
            this.setDefaultPaymentElement();
        }
    }

    private setDefaultPaymentElement(): void {
        this.coursePayments$
            .pipe(
                filter((payments: EduCoursePaymentEntity[]) => Boolean(payments?.length)),
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((coursePayments: EduCoursePaymentEntity[]) => {
                const current = this.paymentElements ?? [];

                const next: PaymentElementLegacy[] = coursePayments.map(
                    (coursePayment: EduCoursePaymentEntity, index: number) => {
                        let isActive = true;

                        if (this.courseTariffId) {
                            isActive = this.courseTariffId === coursePayment.courseTariffId;
                            // eslint-disable-next-line no-prototype-builtins
                        } else if (current.at(index)?.hasOwnProperty('isActive')) {
                            isActive = current.at(index).isActive;
                        }

                        return {
                            type: coursePayment.type,
                            payment_method_id: coursePayment.id,
                            // eslint-disable-next-line no-prototype-builtins
                            isEditableTotalCost: current.at(index)?.hasOwnProperty('isEditableTotalCost')
                                ? current.at(index).isEditableTotalCost
                                : false,
                            currency: coursePayment.currency,
                            amount: coursePayment.price,
                            isActive,
                            eduPaymentMethodId: coursePayment.eduPaymentMethodId,
                        } as PaymentElementLegacy;
                    }
                );

                this.setSkipHistoryEvent.emit();
                this.setPaymentElementsEvent.emit(next);
            });
    }

    public changePaymentElementIsActive(index: number): void {
        const next = (this.paymentElements ?? []).map((element, i) =>
            i === index ? { ...element, isActive: !element.isActive } : element
        );

        this.setPaymentElementsEvent.emit(next);
    }

    public trackByPaymentElementIndex(index: number): number {
        return index;
    }

    public getPaymentElementLabel(paymentElementType: PaymentElement['type']): string {
        if (!paymentElementType) {
            return null;
        }

        return paymentsList[paymentElementType].label;
    }

    public getPaymentElementIcon(paymentElementType: PaymentElement['type']): string {
        if (!paymentElementType) {
            return null;
        }

        return `./assets/img/websites/payment-icons/sm/${paymentTypeNames[paymentElementType]}-sm.svg`;
    }

    public getPaymentElementSubText(paymentElement: PaymentElement): string {
        if (!paymentElement.amount) {
            return this.translateService.instant('payment_amount_invalid');
        }

        return `${this.translateService.instant('control_price')} ${paymentElement.amount} ${paymentElement.currency}`;
    }
}
